import { Fragment, useState, useEffect } from "react";
import { t } from "i18next";

import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import ColumnText from "../molecules/ColumnText";
import { Branded } from "./Branded";
import { Empty } from "./Empty";
import { Fee } from "@/app/fees/fees";

import {
  useFeesUserQuery,
} from "@/app/fees-user/fees-user.api";

type InfoCursoProps = {
  description: string;
  date: string;
};

type CursosProps = {
  id: number | string;
  name: string;
  description: string;
  period: string;
  price: string;
  discount: string;
  matricula: string;
  state: string;
  infoPage: InfoCursoProps;
  currency: string;
  repeatDiscount: string;
  active: number;
};
type option = {
  id: string | number;
  title: string;
  description: string;
  function: () => void;
};

type FeeDescriptionProps = {
  fee: Fee;
};

const FeeDescription = ({ fee }: FeeDescriptionProps) => {
  const getIntervalText = () => {
    if (fee.intervalCount === 1 && fee.interval === "month") {
      return "Mes";
    }
    if (fee.intervalCount === 3 && fee.interval === "month") {
      return "Trimestral";
    }
    if (fee.intervalCount === 6 && fee.interval === "month") {
      return "Semestral";
    }
    if (fee.intervalCount === 1 && fee.interval === "year") {
      return "Anual";
    }
    return "";
  };

  return (
    <span className="text-gray-700">
      {getIntervalText()}
      {fee.intervalCount !== 0 && " + "}
      {fee.trial_period_days ?? 0} días
    </span>
  );
};

const getPeriodText = (
  intervalCount: number,
  interval: string,
  trialPeriodDays: number | null
) => {
  let periodText = "";

  if (intervalCount === 1 && interval === "month") {
    periodText = t("stripe.views.month");
  } else if (intervalCount === 3 && interval === "month") {
    periodText = t("stripe.views.quarterly");
  } else if (intervalCount === 2 && interval === "month") {
    periodText = t("stripe.views.bimonthly");
  } else if (intervalCount === 6 && interval === "month") {
    periodText = t("stripe.views.biannual");
  } else if (intervalCount === 1 && interval === "year") {
    periodText = t("stripe.views.year");
  }

  if (intervalCount !== 0) {
    periodText += " + ";
  }

  periodText += `${trialPeriodDays ?? 0} días`;

  return periodText;
};

export type TableListCuotasCursosUsersProps = {
  cursos: CursosProps[];
  titleBranded: string;
  titleBrandedActiveFee: string;
  OptionsBrandedActiveFee: option[];
  onAssingFee: () => void;
  activeCurrentQuota: any;
  user: any;
};

const TableListCuotasCursosUsers: React.FC<TableListCuotasCursosUsersProps> = ({
  cursos,
  titleBranded,
  titleBrandedActiveFee,
  OptionsBrandedActiveFee,
  onAssingFee,
  activeCurrentQuota,
  user,
}) => {
  const [filteredCursos, setFilteredCursos] = useState(cursos);

  const feesUserQuery = useFeesUserQuery({ branchId: user.branchId, userId: user.id });

  const [feesUser, setFeesUser] = useState<any>(null);

    useEffect(() => {
      if (feesUserQuery.isSuccess) {
        setFeesUser(feesUserQuery.data);
      }
    }, [feesUserQuery]);

  const onSearch = (value: string) => {
    const filtered = cursos.filter((curso) =>
      curso.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCursos(filtered);
  };

  const formatDate = (date: any) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    };
    return new Date(date)
      .toLocaleDateString("en-GB", options)
      .replace(/\//g, "-");
  };

  const getPrice = (fee, statusUser, status) => {
    const priceInit = statusUser
      ? fee.price - fee.price * (statusUser.discount / 100)
      : fee.price;
  
    return status
      ? priceInit - priceInit * (fee.discount / 100)
      : priceInit;
  };
  
  const getPriceProportional = (fee, statusUser, status) => {
    const priceProportionalInit = statusUser
      ? fee.price_proportional - fee.price_proportional * (statusUser.discount / 100)
      : fee.price_proportional;
  
    return status
      ? priceProportionalInit - priceProportionalInit * (fee.discount / 100)
      : priceProportionalInit;
  };

  const getStatusUserDiscount = (user, feeId) => {
    // Simula la lógica de `getStatusUserDiscount` en PHP
    return user.discounts?.find((discount) => discount.feeId === feeId) || null;
  };
  
  const getStatusFeeUserDiscount = (fee, userId) => {
    // Simula la lógica de `getStatusFeeUserDiscount` en PHP
    return fee.userDiscounts?.find((discount) => discount.userId === userId) || null;
  };


  const FeeStatusCell = ({ fee, user, activeCurrentQuota }) => {
    const statusUser = getStatusUserDiscount(user, fee.id);
    const status = getStatusFeeUserDiscount(fee, user.id);
  
    const price = getPrice(fee, statusUser, status);
    const priceProportional = getPriceProportional(fee, statusUser, status);
  
    const feeUser = feesUser?.find(
      (feeUser) => feeUser.feeId === fee.id && feeUser.userId === user.id
    );
    const feeUserStatus = feeUser ? feeUser.status : null;

    return (
      <td>
        {activeCurrentQuota && (
          <div data-fee-info-active={fee.id}>
            {fee.id === activeCurrentQuota.feeId && feeUser && (
              <>
                {feeUser.status === "active" && (
                  <div className="flex items-center">
                    <span className="label label-success label-dot mr-2"></span>
                    <div>
                      <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-green-50 text-green-700 ring-green-600/20">
                        {t("fees.views.state_payment_paid")}
                      </span>
                      <br />
                      <span
                        className="flex justify-start text-gray-500"
                        style={{ fontSize: "12px" }}
                      >
                        {formatDate(feeUser.dateFinish)}
                      </span>
                      {feeUser.daysPause > 0 ? (
                        <>
                          <br />
                          <span className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium bg-gray-50 text-gray-700 ring-gray-600/20">
                            {t("fees.views.state_paused_until")}:{" "}
                            {formatDate(feeUser.datePause)}
                          </span>
                        </>
                      ): null}
                      {/*
                      {activeCurrentQuota.remaining ? (
                        <>
                          <br />
                          <span className="inline-flex justify-start items-center rounded-md px-2 py-1 text-xs font-medium bg-red-50 text-red-700 ring-red-600/20">
                            {t("fees.views.remaining_amount", {
                              amount: activeCurrentQuota.remainingAmount,
                              currency: fee.currency,
                            })}
                          </span>
                        </>
                      ): null}
                      */}
                    </div>
                  </div>
                )}
                {feeUser.status === "unsubscribe" &&
                  new Date(feeUser.date_finish) >= new Date() && (
                    <div className="flex items-center">
                      <span className="label label-warning label-dot mr-2"></span>
                      <div>
                        <span className="text-warning">
                          {t("fees.views.state_payment_low")}
                        </span>
                        <br />
                        <span
                          className="flex justify-start text-gray-500"
                          style={{ fontSize: "12px" }}
                        >
                          {formatDate(feeUser.dateFinish)}
                        </span>
                      </div>
                    </div>
                  )}
                {feeUser.status === "delayed" && (
                  <div className="flex items-center">
                    <span className="label label-danger label-dot mr-2"></span>
                    <div>
                      <span className="text-danger">
                        {t("fees.views.state_payment_delay")}
                      </span>
                      <br />
                      <span
                        className="flex justify-start text-gray-500"
                        style={{ fontSize: "12px" }}
                      >
                        {formatDate(feeUser.dateFinish)}
                      </span>
                      {feeUser.status_aux === "Payment declined" && (
                        <>
                          <br />
                          <span>{t("fees.views.rejected_payment")}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
  
        <div data-fee-info={fee.id}>
          {user.feeNext && fee.id === user.feeNext.feeId && (
            <div className="flex items-center">
              <span className="label label-success label-dot mr-2"></span>
              <div>
                <span className="text-success">
                  {t("fees.views.state_payment_next")}
                </span>
                <br />
                <span style={{ fontSize: "10px" }}>
                  {new Date(user.feeNext.date_init).toLocaleDateString("en-GB")}
                </span>
                {user.feeNext.remaining && (
                  <span style={{ fontSize: "12px" }} className="text-warning">
                    {/*
                    {t("fees.views.remaining_amount", {
                      amount: user.feeNext.remainingAmount,
                      currency: fee.currency,
                    })}
                    */}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </td>
    );
  };

  return (
    <div className="mt-5 overflow-visible">
      <div className="px-2 sm:px-3 lg:px-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
          <div></div>
          <div className="mt-2 sm:mt-0">
            <InputWithCornerHint
              type="text"
              label=""
              placeholder={t("store.views.form.search_by_name")}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
        </div>
        {/* Divider debajo del texto */}
        <div className="flow-root">
          <div className="-mx-4 -my-2 relative sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full px-7 py-2 align-middle">
              {cursos.length === 0 ? (
                <Empty texto={t("customer-to-gym-subscription.views.no_fee")} />
              ) : (
                <Fragment>
                  <table className="min-w-full divide-y divide-gray-300 border-t border-gray-300">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-1/12"
                        >
                          {t("customer-to-gym-subscription.views.name")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-2/12"
                        >
                          {t("customer-to-gym-subscription.views.description")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          {t(
                            "customer-to-gym-subscription.views.renewal_period"
                          )}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          {t("customer-to-gym-subscription.views.price")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          {t("customer-to-gym-subscription.views.discount")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          {t("customer-to-gym-subscription.views.registration")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          {t("fees.views.state_quota")}
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          {t("user.views.pay")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {filteredCursos.map((curso) => (
                        <tr key={curso.id}>
                          <td className="px-3 py-3.5 text-left text-sm text-gray-500 w-2/12">
                            {curso.name}
                          </td>
                          <td className="px-3 py-3.5 text-left text-sm text-gray-500 w-2/12">
                            {curso.description}
                          </td>
                          <ColumnText
                            title={getPeriodText(
                              Number(curso.intervalCount),
                              curso.interval,
                              curso.trialPeriodDays
                            )}
                          />
                          <ColumnText
                            title={`${(curso.price/100)} ${curso.currency.toLocaleUpperCase()}`}
                          />
                          <ColumnText title={`${curso.repeatDiscount} (${curso.discount} %)`} />
                          <ColumnText title={curso.matricula} />
                          <ColumnText
                            title={
                              <>
                                {curso.active === 1
                                  ? t("fees.views.fee_active")
                                  : curso.active === 0
                                  ? t("stripe.views.inactive")
                                  : t("fees.views.fee_inactive")}
                              </>
                            }
                          />
                          <td style={{ width: "80px" }}>
                            {FeeStatusCell({fee: curso, user, activeCurrentQuota: activeCurrentQuota})}
                          </td>
                          <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
                            {/*
                            {curso.infoPage.description === "" ? (
                              <></>
                            ) : (
                              <>
                                <span
                                  className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                                    curso.infoPage.description ===
                                    "Activo hasta"
                                      ? "bg-green-50 text-green-700 ring-green-600/20"
                                      : curso.infoPage.description ===
                                        "Con retraso"
                                      ? "bg-red-50 text-red-700 ring-red-600/20"
                                      : curso.infoPage.description === "De baja"
                                      ? "bg-gray-50 text-gray-700 ring-gray-600/20"
                                      : "bg-gray-50 text-gray-700 ring-gray-600/20"
                                  } ring-1 ring-inset `}
                                >
                                  {curso.infoPage.description}
                                </span>
                                <br />
                                <span className="text-sm">
                                  {curso.infoPage.date}
                                </span>
                              </>
                            )}
                            */}
                          </td>
                          <td className=" whitespace-nowrap py-5 pl-3 pr-4 text-sm font-medium sm:pr-0">
                            {/*
                            {curso.infoPage.description === "" ? (
                              <>
                                <button onClick={onAssingFee} className="text-white text-sm font-semibold inline-flex items-center rounded-l-md rounded-r-md bg-indigo-600 p-2 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 focus:ring-offset-gray-50">
                                  {titleBranded}
                                </button>
                              </>
                            ) : (
                              <>
                                <Branded
                                  title={titleBrandedActiveFee}
                                  publishingOptions={OptionsBrandedActiveFee}
                                />
                              </>
                            )}
                            */}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Fragment>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableListCuotasCursosUsers;
